import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';

interface SitePopupProps {
  userId: Id<"users">;
}

const SitePopup: React.FC<SitePopupProps> = ({ userId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const viewedPopupIds = useRef<Set<string>>(new Set());

  const popups = useQuery(api.popups.getAllActiveForUser, { userId });
  const recordInteraction = useMutation(api.popups.recordInteraction);

  const activePopup = useMemo(() => {
    if (!popups || popups.length === 0) return null;
    return popups[currentIndex] ?? null;
  }, [popups, currentIndex]);
  const [isMinimized, setIsMinimized] = useState(false);

  // useEffect(() => {
  //   if (!activePopup) return;
  //   const key = `sitePopup:minimized:${userId}:${String(activePopup._id)}`;
  //   const saved = window.localStorage.getItem(key);
  //   setIsMinimized(saved === 'false');
  // }, [activePopup, userId]);
  useEffect(() => {
    setIsMinimized(false);
  }, []);


  useEffect(() => {
    if (!activePopup) return;
    const key = `sitePopup:minimized:${userId}:${String(activePopup._id)}`;
    window.localStorage.setItem(key, String(isMinimized));
  }, [isMinimized, activePopup, userId]);

  useEffect(() => {
    if (!popups || popups.length === 0) {
      setIsVisible(false);
      setCurrentIndex(0);
      viewedPopupIds.current.clear();
      return;
    }

    setCurrentIndex(0);
    viewedPopupIds.current.clear();
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, [popups]);

  useEffect(() => {
    if (!activePopup || !isVisible) return;
    const popupId = String(activePopup._id);
    if (viewedPopupIds.current.has(popupId)) return;
    viewedPopupIds.current.add(popupId);

    recordInteraction({
      popupId: activePopup._id,
      userId,
      action: 'view',
    }).catch(console.error);
  }, [activePopup, isVisible, recordInteraction, userId]);


  if (!activePopup) return null;

  const handleCallClick = async () => {
    if (activePopup) {
      try {
        await recordInteraction({
          popupId: activePopup._id,
          userId,
          action: 'call_clicked',
        });
      } catch (error) {
        console.error('Failed to track call click:', error);
      }
    }

    if (activePopup?.phoneNumber) {
      window.location.href = `tel:${activePopup.phoneNumber}`;
    }
  };

  if (!activePopup || !isVisible) return null;

  return (
    <>
      {/* no onClick on backdrop */}
      {!isMinimized && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" />
      )}

      <div
        className={
          isMinimized
            ? 'fixed bottom-4 left-1/2 scale-[2.9]  -translate-x-1/2 z-50 w-[min(92vw,420px)]'
            : 'fixed inset-0 z-50 flex items-center justify-center p-4'
        }
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative transform transition-all">
          {/* remove close X button */}

          <div className="text-center">
            <div className="pl-10 flex">
              <div className="w-80 flex-auto">
                <h3 className="text-xl font-medium font-sans text-gray-900 mb-2">{activePopup.title}</h3>
              </div>
              <div className=" flex-auto">
                <button
                  type="button"
                  onClick={() => setIsMinimized((v) => !v)}
                  className="flex-auto flex justify-end"
                  aria-label={isMinimized ? 'Expand popup' : 'Minimize popup'}
                >

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                </button>

              </div>
            </div>

            <p className="text-gray-600 mb-6">{activePopup.message}</p>

            {activePopup.phoneNumber && activePopup.showPhone && (
              <button
                onClick={handleCallClick}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                Call {activePopup.phoneNumber}
              </button>
            )}

            <p className="mt-4 text-xs text-gray-500">
              This announcement stays visible until you call us to discuss your system.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SitePopup;
